import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  Modal,
  Badge,
  Spinner,
  Alert,
  InputGroup,
} from 'react-bootstrap';
import { FaTruck, FaPlus, FaEdit, FaSearch, FaUniversity, FaTrash } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import depositosApi from '../../services/api/depositosApi';
import 'react-toastify/dist/ReactToastify.css';

const emptyForm = {
  nombre: '',
  numero_documento: '',
  excluido_retencion: false,
  activo: true,
  contacto_nombre: '',
  contacto_email: '',
  contacto_telefono: '',
  direccion: '',
};

const emptyCuentaForm = {
  banco: '',
  numero_cuenta: '',
  tipo_cuenta_bancaria_id: '',
  codigo_cuenta: '',
  moneda: 'PEN',
  es_principal: false,
  activo: true,
};

const ProveedoresCorporativo = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [showCuentasModal, setShowCuentasModal] = useState(false);
  const [selectedProveedor, setSelectedProveedor] = useState(null);
  const [cuentas, setCuentas] = useState([]);
  const [tiposCuenta, setTiposCuenta] = useState([]);
  const [cuentaEditing, setCuentaEditing] = useState(null);
  const [cuentaForm, setCuentaForm] = useState(emptyCuentaForm);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await depositosApi.getBeneficiarios('proveedor', false);
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Error al cargar proveedores');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    const loadTiposCuenta = async () => {
      try {
        const response = await depositosApi.getTiposCuentaBancaria(true);
        setTiposCuenta(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error(error);
      }
    };
    loadTiposCuenta();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleCuentaInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCuentaForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const resetForm = () => {
    setEditing(null);
    setFormData(emptyForm);
  };

  const resetCuentaForm = () => {
    setCuentaEditing(null);
    setCuentaForm(emptyCuentaForm);
  };

  const openNew = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormData({
      nombre: row.nombre || '',
      numero_documento: row.numero_documento || '',
      excluido_retencion: !!row.excluido_retencion,
      activo: row.activo !== false,
      contacto_nombre: row.contacto_nombre || '',
      contacto_email: row.contacto_email || '',
      contacto_telefono: row.contacto_telefono || '',
      direccion: row.direccion || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const nombre = formData.nombre.trim();
      if (!nombre) {
        toast.error('El nombre es obligatorio');
        setLoading(false);
        return;
      }
      if (editing) {
        await depositosApi.updateBeneficiario(editing.id, {
          nombre,
          numero_documento: formData.numero_documento?.trim() || null,
          excluido_retencion: !!formData.excluido_retencion,
          activo: !!formData.activo,
          contacto_nombre: formData.contacto_nombre?.trim() || null,
          contacto_email: formData.contacto_email?.trim() || null,
          contacto_telefono: formData.contacto_telefono?.trim() || null,
          direccion: formData.direccion?.trim() || null,
        });
        toast.success('Proveedor actualizado');
      } else {
        await depositosApi.createBeneficiario({
          tipo: 'proveedor',
          nombre,
          numero_documento: formData.numero_documento?.trim() || null,
          excluido_retencion: !!formData.excluido_retencion,
          activo: true,
          contacto_nombre: formData.contacto_nombre?.trim() || null,
          contacto_email: formData.contacto_email?.trim() || null,
          contacto_telefono: formData.contacto_telefono?.trim() || null,
          direccion: formData.direccion?.trim() || null,
        });
        toast.success(
          'Proveedor creado. Agregue cuentas bancarias desde Depósitos al registrar un pago, si aplica.'
        );
      }
      setShowModal(false);
      resetForm();
      loadItems();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  const loadCuentasProveedor = async (proveedorId) => {
    const response = await depositosApi.getCuentasByBeneficiario(proveedorId, true, true);
    setCuentas(Array.isArray(response.data) ? response.data : []);
  };

  const openCuentas = async (proveedor) => {
    try {
      setSelectedProveedor(proveedor);
      setShowCuentasModal(true);
      resetCuentaForm();
      await loadCuentasProveedor(proveedor.id);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Error al cargar cuentas bancarias');
    }
  };

  const openCuentaEdit = (cuenta) => {
    setCuentaEditing(cuenta);
    setCuentaForm({
      banco: cuenta.banco || '',
      numero_cuenta: cuenta.numero_cuenta || '',
      tipo_cuenta_bancaria_id: cuenta.tipo_cuenta_bancaria_id ? String(cuenta.tipo_cuenta_bancaria_id) : '',
      codigo_cuenta: cuenta.codigo_cuenta || '',
      moneda: cuenta.moneda || 'PEN',
      es_principal: !!cuenta.es_principal,
      activo: cuenta.activo !== false,
    });
  };

  const handleCuentaSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProveedor) return;

    try {
      setLoading(true);
      const payload = {
        beneficiario_id: selectedProveedor.id,
        banco: cuentaForm.banco?.trim(),
        numero_cuenta: cuentaForm.numero_cuenta?.trim(),
        tipo_cuenta_bancaria_id: parseInt(cuentaForm.tipo_cuenta_bancaria_id, 10),
        codigo_cuenta: cuentaForm.codigo_cuenta?.trim() || null,
        moneda: cuentaForm.moneda?.trim() || 'PEN',
        es_principal: !!cuentaForm.es_principal,
        activo: !!cuentaForm.activo,
      };
      if (!payload.banco || !payload.numero_cuenta || !payload.tipo_cuenta_bancaria_id) {
        toast.error('Banco, número de cuenta y tipo de cuenta son obligatorios');
        return;
      }

      if (cuentaEditing) {
        await depositosApi.updateCuentaBancaria(cuentaEditing.id, {
          banco: payload.banco,
          numero_cuenta: payload.numero_cuenta,
          tipo_cuenta_bancaria_id: payload.tipo_cuenta_bancaria_id,
          codigo_cuenta: payload.codigo_cuenta,
          moneda: payload.moneda,
          es_principal: payload.es_principal,
          activo: payload.activo,
        });
        toast.success('Cuenta bancaria actualizada');
      } else {
        await depositosApi.createCuentaBancaria(payload);
        toast.success('Cuenta bancaria registrada');
      }
      resetCuentaForm();
      await loadCuentasProveedor(selectedProveedor.id);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Error al guardar cuenta bancaria');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCuenta = async (cuenta) => {
    if (!selectedProveedor) return;
    if (!window.confirm(`¿Eliminar la cuenta ${cuenta.numero_cuenta}?`)) return;
    try {
      setLoading(true);
      await depositosApi.deleteCuentaBancaria(cuenta.id);
      toast.success('Cuenta bancaria eliminada');
      await loadCuentasProveedor(selectedProveedor.id);
      if (cuentaEditing && cuentaEditing.id === cuenta.id) {
        resetCuentaForm();
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Error al eliminar cuenta bancaria');
    } finally {
      setLoading(false);
    }
  };

  const filtered = items.filter((b) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      (b.nombre && b.nombre.toLowerCase().includes(q)) ||
      (b.codigo && String(b.codigo).toLowerCase().includes(q)) ||
      (b.numero_documento && String(b.numero_documento).toLowerCase().includes(q))
    );
  });

  return (
    <Container fluid className="p-4">
      <ToastContainer />
      <Row className="mb-4">
        <Col>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <h3 className="mb-1">
                <FaTruck className="me-2 text-primary" />
                Proveedores (corporativo)
              </h3>
              <p className="text-muted mb-0 small">
                Mantenimiento de proveedores para depósitos. Indique si está excluido de retención del agente de
                retención (Perú).
              </p>
            </div>
            <Button variant="primary" onClick={openNew}>
              <FaPlus className="me-2" />
              Nuevo proveedor
            </Button>
          </div>
        </Col>
      </Row>

      <Card className="mb-3 shadow-sm">
        <Card.Body>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              placeholder="Buscar por nombre, código o documento…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Card.Body>
      </Card>

      <Card className="shadow-sm">
        <Card.Body>
          {loading && items.length === 0 ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped hover bordered size="sm">
                <thead className="table-light">
                  <tr>
                    <th>Código</th>
                    <th>Razón social / nombre</th>
                    <th>Documento</th>
                    <th className="text-center">Excl. retención</th>
                    <th className="text-center">Activo</th>
                    <th className="text-center" style={{ width: '100px' }}>
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        No hay proveedores que coincidan con la búsqueda.
                      </td>
                    </tr>
                  ) : (
                    filtered.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <code>{row.codigo}</code>
                        </td>
                        <td>{row.nombre}</td>
                        <td>{row.numero_documento || '—'}</td>
                        <td className="text-center">
                          {row.excluido_retencion ? (
                            <Badge bg="success">Sí</Badge>
                          ) : (
                            <Badge bg="secondary">No</Badge>
                          )}
                        </td>
                        <td className="text-center">
                          {row.activo ? <Badge bg="primary">Sí</Badge> : <Badge bg="warning">No</Badge>}
                        </td>
                        <td className="text-center">
                          <Button variant="outline-primary" size="sm" title="Editar" onClick={() => openEdit(row)}>
                            <FaEdit />
                          </Button>
                          <Button
                            variant="outline-success"
                            size="sm"
                            title="Cuentas bancarias"
                            className="ms-1"
                            onClick={() => openCuentas(row)}
                          >
                            <FaUniversity />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => { setShowModal(false); resetForm(); }} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Editar proveedor' : 'Nuevo proveedor'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Alert variant="info" className="py-2 small mb-3">
              Las cuentas bancarias se asocian al registrar depósitos o desde el módulo de beneficiarios general si lo
              utiliza.
            </Alert>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre / razón social *</Form.Label>
                  <Form.Control
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleInputChange}
                    required
                    maxLength={255}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Documento (RUC, etc.)</Form.Label>
                  <Form.Control
                    name="numero_documento"
                    value={formData.numero_documento}
                    onChange={handleInputChange}
                    maxLength={50}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="excluido_retencion"
                label="Proveedor excluido de retención (agente de retención)"
                checked={!!formData.excluido_retencion}
                onChange={handleInputChange}
              />
            </Form.Group>
            {editing && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="activo"
                  label="Activo"
                  checked={!!formData.activo}
                  onChange={handleInputChange}
                />
              </Form.Group>
            )}
            <hr />
            <p className="text-muted small mb-2">Contacto (opcional)</p>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Contacto</Form.Label>
                  <Form.Control
                    name="contacto_nombre"
                    value={formData.contacto_nombre}
                    onChange={handleInputChange}
                    maxLength={255}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="contacto_email"
                    value={formData.contacto_email}
                    onChange={handleInputChange}
                    maxLength={255}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Teléfono</Form.Label>
                  <Form.Control
                    name="contacto_telefono"
                    value={formData.contacto_telefono}
                    onChange={handleInputChange}
                    maxLength={50}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-0">
              <Form.Label>Dirección</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="direccion"
                value={formData.direccion}
                onChange={handleInputChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" type="button" onClick={() => { setShowModal(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : 'Guardar'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={showCuentasModal}
        onHide={() => {
          setShowCuentasModal(false);
          setSelectedProveedor(null);
          setCuentas([]);
          resetCuentaForm();
        }}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Cuentas bancarias de {selectedProveedor?.nombre || 'proveedor'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col lg={5}>
              <Card className="mb-3">
                <Card.Header>{cuentaEditing ? 'Editar cuenta' : 'Nueva cuenta bancaria'}</Card.Header>
                <Card.Body>
                  <Form onSubmit={handleCuentaSubmit}>
                    <Form.Group className="mb-2">
                      <Form.Label>Banco *</Form.Label>
                      <Form.Control
                        name="banco"
                        value={cuentaForm.banco}
                        onChange={handleCuentaInputChange}
                        required
                        maxLength={100}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Número de cuenta *</Form.Label>
                      <Form.Control
                        name="numero_cuenta"
                        value={cuentaForm.numero_cuenta}
                        onChange={handleCuentaInputChange}
                        required
                        maxLength={100}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Tipo de cuenta *</Form.Label>
                      <Form.Select
                        name="tipo_cuenta_bancaria_id"
                        value={cuentaForm.tipo_cuenta_bancaria_id}
                        onChange={handleCuentaInputChange}
                        required
                      >
                        <option value="">Seleccione...</option>
                        {tiposCuenta.map((tipo) => (
                          <option key={tipo.id} value={tipo.id}>
                            {tipo.nombre}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Moneda</Form.Label>
                      <Form.Control
                        name="moneda"
                        value={cuentaForm.moneda}
                        onChange={handleCuentaInputChange}
                        maxLength={10}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Código de cuenta (opcional)</Form.Label>
                      <Form.Control
                        name="codigo_cuenta"
                        value={cuentaForm.codigo_cuenta}
                        onChange={handleCuentaInputChange}
                        maxLength={100}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Check
                        type="checkbox"
                        name="es_principal"
                        label="Cuenta principal"
                        checked={!!cuentaForm.es_principal}
                        onChange={handleCuentaInputChange}
                      />
                    </Form.Group>
                    {cuentaEditing && (
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          name="activo"
                          label="Activa"
                          checked={!!cuentaForm.activo}
                          onChange={handleCuentaInputChange}
                        />
                      </Form.Group>
                    )}
                    <div className="d-flex gap-2 justify-content-end">
                      {cuentaEditing && (
                        <Button type="button" variant="secondary" onClick={resetCuentaForm}>
                          Cancelar edición
                        </Button>
                      )}
                      <Button type="submit" variant="primary" disabled={loading}>
                        {loading ? <Spinner size="sm" /> : cuentaEditing ? 'Actualizar' : 'Agregar'}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={7}>
              <Card>
                <Card.Header>Cuentas registradas</Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table bordered hover size="sm">
                      <thead className="table-light">
                        <tr>
                          <th>Banco</th>
                          <th>Número</th>
                          <th>Tipo</th>
                          <th>Moneda</th>
                          <th className="text-center">Principal</th>
                          <th className="text-center">Activo</th>
                          <th className="text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cuentas.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center text-muted py-3">
                              No hay cuentas bancarias registradas.
                            </td>
                          </tr>
                        ) : (
                          cuentas.map((cuenta) => (
                            <tr key={cuenta.id}>
                              <td>{cuenta.banco}</td>
                              <td>{cuenta.numero_cuenta}</td>
                              <td>{cuenta.tipo_cuenta_nombre || '—'}</td>
                              <td>{cuenta.moneda || '—'}</td>
                              <td className="text-center">
                                {cuenta.es_principal ? <Badge bg="success">Sí</Badge> : <Badge bg="secondary">No</Badge>}
                              </td>
                              <td className="text-center">
                                {cuenta.activo ? <Badge bg="primary">Sí</Badge> : <Badge bg="warning">No</Badge>}
                              </td>
                              <td className="text-center">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-1"
                                  onClick={() => openCuentaEdit(cuenta)}
                                >
                                  <FaEdit />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => handleDeleteCuenta(cuenta)}
                                >
                                  <FaTrash />
                                </Button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default ProveedoresCorporativo;
